import { ApolloError, useQuery } from "@apollo/client";
import React, { useEffect, useMemo, useState } from "react";
import SCHEDULES_QUERY from "src/graphql/query/schedulesQuery";
// import usePageType from "./usePageType";
import moment from "moment";
import { useRouter } from "next/router";
import { getCookie } from "cookies-next";
import useRouteChange from "./useRouterChange";

function useSchedules(serviceOrCategoryId: string): {
  error: ApolloError;
  loading: boolean;
  schedules: ScheduleCardPorps[];
  page: number;
  totalPage: number;
  // eslint-disable-next-line
  handlePageChange: (event: React.ChangeEvent<{}>, value: number) => void;
} {
  // const { pageType } = usePageType();

  const router = useRouter();
  const { country, city, course, category } = router.query;
  const geo = getCookie("geoLocation");
  const pageType = useMemo(() => {
    if (category && !course && !country) {
      return "GENERIC COURSE";
    }
    if (category && !course && country) {
      return "COUNTRY COURSE";
    }
    if (category && course && !country) {
      return "GENERIC COURSE";
    }
    if (category && course && country) {
      return "COUNTRY COURSE";
    }
    if (category && course && country && city) {
      return "CITY COURSE";
    }
  }, [country, city, course, category]);

  const { data, error, loading } = useQuery(SCHEDULES_QUERY, {
    variables: {
      serviceOrCategoryId: parseInt(serviceOrCategoryId),
      pageType: pageType,
      countryCode: country ? country : geo ? geo : "us",
      citySlug: city ? city : "",
    },
  });

  const paginator = (items, currentPage, perPageItems) => {
    let page = currentPage || 1,
      per_page = perPageItems || 10,
      offset = (page - 1) * per_page,
      paginatedItems = items.slice(offset).slice(0, perPageItems),
      total_pages = Math.ceil(items.length / per_page);

    return {
      page: page,
      perPage: per_page,
      previousPage: page - 1 ? page - 1 : null,
      nextPage: total_pages > page ? page + 1 : null,
      total: items.length,
      totalPages: total_pages,
      data: paginatedItems,
    };
  };

  const results = useMemo(() => {
    if (!data) return []; // if data is not found returning [] to avoiding error
    return data.scheduleServiceCountry.map((item) => ({
      mode: item.mode,
      dates: item.schedule_dates_string.split("|").map((date) => {
        const dateString = moment(date, "YYYY-MM-DD").format("Do"); //Do will add like 10th in each date string
        const dateObject = {
          date: dateString.substring(0, dateString.length - 2),
          anth: dateString.substring(dateString.length - 2, dateString.length),
        };
        return `${dateObject.date}<sup>${dateObject.anth}</sup>`;
      }),
      month: moment(item.schedule_start_date).format("MMMM"), // for half name MMM for full name MMMM
      startDate: moment(item.schedule_start_date).format("YYYY-MM-DD"), // Date Format
      endDate: moment(item.schedule_end_date).format("YYYY-MM-DD"), // Date Format
      isSameYear: moment(item.schedule_start_date).isSame(
        item.schedule_end_date,
        "year"
      ),
      startMonth: moment(item.schedule_start_date).format("MMM"),
      endMonth: moment(item.schedule_end_date).format("MMM"),
      schedule_title: item.schedule_title,
      serviceLevel: item.service_level,
      fastFilling: moment(item.schedule_start_date)
        .subtract(20, "days")
        .isBefore(moment(item.schedule_start_date))
        ? "Fast Filling! Hurry Up."
        : false,
      strikOutPrice:
        item.scheduleDetails.length > 0
          ? Math.round(
              item.scheduleDetails[0].sellingPrice +
                item.scheduleDetails[0].sellingPrice / 10
            )
          : "",
      ...(item.scheduleDetails.length > 0
        ? {
            timing: `${moment(
              item.scheduleDetails[0].startTime,
              "HH:mm:ss"
            ).format("H:mm")} - ${moment(
              item.scheduleDetails[0].endTime,
              "HH:mm:ss"
            ).format("H:mm")} (${item.scheduleDetails[0].timezone})`,
            sellingPrice: item.scheduleDetails[0].sellingPrice,
            strikOutPrice: Math.round(
              item.scheduleDetails[0].sellingPrice +
                item.scheduleDetails[0].sellingPrice / 10
            ),
            serviceId: item.scheduleDetails[0].service_id,
            scheduleId: item.scheduleDetails[0].schedule_id,
            scheduleDetailId: item.scheduleDetails[0].schedule_detail_id,
            currencyCode: item.scheduleDetails[0].currency_code,
            hours: moment
              .duration(
                moment(item.scheduleDetails[0].endTime, ["h:m a", "H:m"]).diff(
                  moment(item.scheduleDetails[0].startTime, ["h:m a", "H:m"])
                )
              )
              .asHours(),
          }
        : {
            timing: "",
            sellingPrice: "",
            strikOutPrice: "",
            serviceId: "",
            scheduleId: "",
            scheduleDetailId: "",
            currencyCode: "",
            hours: "",
          }),
    }));
  }, [data]);

  const [schedules, setSchedules] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPage, setTotalPage] = useState(0);

  useEffect(() => {
    const paginatedData = paginator(results, page, 5);
    setSchedules(paginatedData.data);
    setTotalPage(paginatedData.totalPages);
  }, [results, page]);

  useRouteChange(null, () => {
    setPage(1);
  });

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPage(value);
  };
  return { error, loading, schedules, page, totalPage, handlePageChange };
}

export default useSchedules;
